/**
 * Multi-year projection: quarterly SLA, conflicts and utilisation under a
 * growth model, the saturation point and recommendations.
 */
import { useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { TextButton } from '@/components/ui/primitives';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { runProjection } from '../../analysis';
import type { BudgetProjection } from '../../analysis/types';
import { setBudgetProjection } from '../../export/exportStore';
import { hasTle, toEngineAntenna, toEngineMission } from '../../LoaderRuntime';
import { getOrbitEngine } from '../../engine';
import { useLoader } from '../../store';
import type { Antenna, Mission } from '../../types';
import { pct } from '../format';

type Growth = 'aggressive' | 'standard' | 'flat';

function heat(util: number): string {
  if (util > 90) return 'bg-warning/50';
  if (util > 70) return 'bg-caution/45';
  if (util > 40) return 'bg-nominal/35';
  if (util > 0) return 'bg-sim/20';
  return '';
}

export function BudgetPanel() {
  const dataset = useLoader((s) => s.dataset);
  const activeMissionIds = useLoader((s) => s.activeMissionIds);
  const activeAntennaIds = useLoader((s) => s.activeAntennaIds);
  const [startFY, setStartFY] = useState(2026);
  const [endFY, setEndFY] = useState(2029);
  const [growth, setGrowth] = useState<Growth>('standard');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [projection, setProjection] = useState<BudgetProjection | null>(null);

  const run = async () => {
    if (!dataset) return;
    const activeM = new Set(activeMissionIds);
    const activeA = new Set(activeAntennaIds);
    const missions = dataset.missions.filter((m) => activeM.has(m.id));
    const antennas = dataset.antennas.filter((a) => activeA.has(a.id));
    if (missions.length === 0 || antennas.length === 0) {
      toast('warn', 'Select at least one mission and one antenna.');
      return;
    }
    setRunning(true);
    setProgress('Starting');
    try {
      const engine = getOrbitEngine();
      const result = await runProjection({
        missions,
        antennas,
        startFY,
        endFY,
        growthModel: growth,
        isToggleEnabled: (id: string) => useLoader.getState().toggles[id] ?? true,
        computeContactWindows: (ms: Mission[], as: Antenna[], startMs: number, endMs: number) =>
          engine.computeContactWindows({
            missions: ms.filter(hasTle).map(toEngineMission),
            antennas: as.map(toEngineAntenna),
            startMs,
            endMs,
          }),
        onProgress: (p: { percent: number; message: string }) => setProgress(p.message),
      });
      setProjection(result);
      setBudgetProjection(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[loader] budget projection failed:', err);
      toast('error', `Projection failed: ${message}`);
    } finally {
      setRunning(false);
      setProgress('');
    }
  };

  const antennaName = (id: string) => dataset?.antennas.find((a) => a.id === id)?.shortName ?? id;
  const rows = projection?.yearlyResults ?? [];
  const antennaIds = rows.length ? [...new Set(rows.flatMap((r) => Object.keys(r.utilization)))] : [];

  return (
    <div className="flex h-full flex-col text-data">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-hairline px-panel py-2">
        <label className="flex items-center gap-1 text-ink-3">
          From
          <select
            aria-label="Start fiscal year"
            value={startFY}
            onChange={(e) => setStartFY(Number(e.target.value))}
            className="rounded-control border border-hairline bg-inset px-2 py-1 text-ink"
          >
            {[2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>
                FY{y}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-ink-3">
          to
          <select
            aria-label="End fiscal year"
            value={endFY}
            onChange={(e) => setEndFY(Number(e.target.value))}
            className="rounded-control border border-hairline bg-inset px-2 py-1 text-ink"
          >
            {[2027, 2028, 2029, 2030].map((y) => (
              <option key={y} value={y}>
                FY{y}
              </option>
            ))}
          </select>
        </label>
        <select
          aria-label="Growth model"
          value={growth}
          onChange={(e) => setGrowth(e.target.value as Growth)}
          className="rounded-control border border-hairline bg-inset px-2 py-1 text-ink"
        >
          <option value="flat">Flat demand</option>
          <option value="standard">Standard growth (5%)</option>
          <option value="aggressive">Aggressive growth (10%)</option>
        </select>
        <TextButton
          onClick={() => void run()}
          disabled={running || !dataset}
          className="flex items-center gap-1 bg-accent-soft text-accent"
        >
          {running ? <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" /> : <Play aria-hidden className="h-3.5 w-3.5" />}{' '}
          Project
        </TextButton>
        <span role="status" className="text-ink-3">
          {running ? progress : ''}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-panel scrollbar-thin">
        {!projection ? (
          <div className="text-ink-3">
            Project quarterly SLA and utilisation across fiscal years with the chosen growth model. Each
            quarter samples one week of contacts.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {projection.saturationPoint && (
              <div className="rounded-control border border-warning/50 bg-warning/10 px-3 py-2 text-ink">
                Saturation by {projection.saturationPoint.label}:{' '}
                {antennaName(projection.saturationPoint.antennaId)} reaches{' '}
                {pct(projection.saturationPoint.maxUtil)}.
              </div>
            )}
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">
                Network SLA by quarter
              </div>
              <div className="flex h-28 items-end gap-1" role="list" aria-label="Network SLA by quarter">
                {rows.map((r) => (
                  <div
                    key={r.label}
                    role="listitem"
                    aria-label={`${r.label}: SLA ${pct(r.sla)}, ${r.conflicts} conflicts, ${r.missionCount} missions`}
                    className="flex flex-1 flex-col items-center gap-1"
                    title={`${r.label}: SLA ${pct(r.sla)}, ${r.conflicts} conflicts, ${r.missionCount} missions`}
                  >
                    <div className="font-mono text-[10px] text-ink-2">{pct(r.sla)}</div>
                    <div
                      className={cn(
                        'w-full rounded-t-[3px]',
                        r.sla >= 95 ? 'bg-nominal' : r.sla >= 70 ? 'bg-caution' : 'bg-warning',
                      )}
                      style={{ height: `${Math.max(2, r.sla)}%` }}
                    />
                    <div className="text-[10px] text-ink-3">{r.label.replace('FY', '')}</div>
                  </div>
                ))}
              </div>
            </div>
            {antennaIds.length > 0 && (
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">
                  Antenna utilisation
                </div>
                <table className="border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className="px-cell-x py-cell-y text-left text-data-sm font-normal text-ink-3">Antenna</th>
                      {rows.map((r) => (
                        <th
                          key={r.label}
                          className="px-1 py-cell-y text-center font-mono text-data-sm font-normal text-ink-3"
                        >
                          {r.label.replace('FY', '')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {antennaIds.map((id) => (
                      <tr key={id}>
                        <th scope="row" className="px-cell-x py-cell-y text-left font-normal text-ink">
                          {antennaName(id)}
                        </th>
                        {rows.map((r) => (
                          <td
                            key={r.label}
                            className={cn(
                              'px-1 py-cell-y text-center font-mono text-ink',
                              heat(r.utilization[id] ?? 0),
                            )}
                          >
                            {pct(r.utilization[id] ?? 0)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {projection.recommendations.length > 0 && (
              <ul className="list-disc space-y-1 pl-5 text-ink-2">
                {projection.recommendations.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
